import React, { useState, useContext } from 'react';
import { ServerConfigContext } from '../context/ServerConfigContext';
import { saveConfigToServerFile } from '../utils/configManager';
import './SettingsPage.css';

const SettingsPage = () => {
  const { 
    // 서버 설정 관련
    servers, 
    addServer, 
    updateServer, 
    deleteServer, 
    toggleServerActive,
    getServer,
    
    // 거래대금 표 설정 관련
    tableSettings,
    updateTableSettings,
    addFilterKeyword,
    removeFilterKeyword,
    clearFilterKeywords,
    addThemeFilterKeyword,
    removeThemeFilterKeyword,
    clearThemeFilterKeywords,
    resetTableSettings,
    
    // 통합 설정 관리
    exportAppConfigs,
    importAppConfigs
  } = useContext(ServerConfigContext);
  
  const [isAddingServer, setIsAddingServer] = useState(false);
  const [editingServerId, setEditingServerId] = useState(null);
  const [saveMessage, setSaveMessage] = useState('');
  const [autoSaveMessage, setAutoSaveMessage] = useState('');
  const [activeTab, setActiveTab] = useState('server'); // 'server' 또는 'table'
  const [newFilterKeyword, setNewFilterKeyword] = useState('');
  const [newThemeFilterKeyword, setNewThemeFilterKeyword] = useState('');
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    baseUrl: '',
    domain: '',
    apiTimeout: 10000,
    isActive: false
  });

  // 설정 변경 시 자동으로 app-config.json 파일 업데이트
  const autoSaveToConfigFile = async () => {
    try {
      const configData = exportAppConfigs();
      const result = await saveConfigToServerFile(configData);

      if (result.success) {
        setAutoSaveMessage('✅ 설정이 파일에 자동 저장되었습니다.');
        setTimeout(() => setAutoSaveMessage(''), 2000);
      }
    } catch (error) {
      console.error('자동 저장 실패:', error);
      setAutoSaveMessage('❌ 자동 저장 실패');
      setTimeout(() => setAutoSaveMessage(''), 3000);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const resetForm = () => {
    setFormData({
      id: '',
      name: '',
      baseUrl: '',
      domain: '',
      apiTimeout: 10000,
      isActive: false
    });
    setIsAddingServer(false);
    setEditingServerId(null);
  };

  const handleAddServer = () => {
    setIsAddingServer(true);
    setFormData({
      id: '',
      name: '',
      baseUrl: '',
      domain: '',
      apiTimeout: 10000,
      isActive: false
    });
    setEditingServerId(null);
  };

  const handleEditServer = (serverId) => {
    const server = getServer(serverId);
    if (server) {
      setFormData(server);
      setEditingServerId(serverId);
      setIsAddingServer(false);
    }
  };

  const handleSaveServer = () => {
    let result;
    if (editingServerId) {
      result = updateServer(editingServerId, formData);
    } else {
      result = addServer(formData);
    }
    
    if (result.success) {
      setSaveMessage(result.message);
      resetForm();
    } else {
      setSaveMessage(result.message);
    }
    
    setTimeout(() => setSaveMessage(''), 3000);
  };

  const handleDeleteServer = (serverId) => {
    if (window.confirm('정말로 이 서버를 삭제하시겠습니까?')) {
      const result = deleteServer(serverId);
      setSaveMessage(result.message);
      setTimeout(() => setSaveMessage(''), 3000);
    }
  };

  const handleTestConnection = async (server) => {
    try {
      const response = await fetch(`${server.baseUrl}/api/health`);
      if (response.ok) {
        setSaveMessage(`${server.name} 연결이 성공했습니다.`);
      } else {
        setSaveMessage(`${server.name} 연결에 실패했습니다.`);
      }
    } catch (error) {
      setSaveMessage(`${server.name} 연결에 실패했습니다: ${error.message}`);
    }
    setTimeout(() => setSaveMessage(''), 3000);
  };


  // 거래대금 표 설정 관련 함수들
  const handleTableSettingChange = (key, value) => {
    updateTableSettings({
      [key]: {
        ...tableSettings[key],
        ...value
      }
    });
  };

  const handleAddFilterKeyword = () => {
    if (newFilterKeyword.trim()) {
      addFilterKeyword(newFilterKeyword.trim());
      setNewFilterKeyword('');
      // 자동 저장
      setTimeout(() => autoSaveToConfigFile(), 100);
    }
  };

  const handleAddThemeFilterKeyword = () => {
    if (newThemeFilterKeyword.trim()) {
      addThemeFilterKeyword(newThemeFilterKeyword.trim());
      setNewThemeFilterKeyword('');
      // 자동 저장
      setTimeout(() => autoSaveToConfigFile(), 100);
    }
  };

  // 자동 저장을 포함한 키워드 제거 핸들러
  const handleRemoveFilterKeyword = (keyword) => {
    removeFilterKeyword(keyword);
    setTimeout(() => autoSaveToConfigFile(), 100);
  };

  const handleRemoveThemeFilterKeyword = (keyword) => {
    removeThemeFilterKeyword(keyword);
    setTimeout(() => autoSaveToConfigFile(), 100);
  };

  const handleExportConfig = () => {
    const result = exportAppConfigs();
    setSaveMessage(result.message);
    setTimeout(() => setSaveMessage(''), 3000);
  };

  const handleImportConfig = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const result = await importAppConfigs(file);
    setSaveMessage(result.message);
    setTimeout(() => setSaveMessage(''), 3000);
    
    event.target.value = '';
  };

  return (
    <div className="page-container">
      <div className="settings-container">
        <div className="settings-header">
          <h2>설정</h2>
          <p>서버 설정 및 거래대금 표 관련 설정을 관리합니다</p>
          
          {/* 탭 네비게이션 */}
          <div className="settings-tabs">
            <button 
              className={`tab-btn ${activeTab === 'server' ? 'active' : ''}`}
              onClick={() => setActiveTab('server')}
            >
              서버 설정
            </button>
            <button 
              className={`tab-btn ${activeTab === 'table' ? 'active' : ''}`}
              onClick={() => setActiveTab('table')}
            >
              거래대금 표 설정
            </button>
          </div>
          
          {/* 통합 파일 가져오기/내보내기 */}
          <div className="config-file-actions">
            <button onClick={handleExportConfig} className="btn-export">
              📄 설정 다운로드
            </button>
            <label htmlFor="import-config" className="btn-import">
              📁 설정 업로드
              <input
                id="import-config"
                type="file"
                accept=".json"
                onChange={handleImportConfig}
                style={{ display: 'none' }}
              />
            </label>
          </div>
          
          {/* 설정 파일 안내 */}
          <div className="config-file-info">
            <h4>🔄 자동 저장 설정</h4>
            <div className="info-content">
              <p><strong>설정 자동 복구:</strong></p>
              <ol>
                <li>"설정 다운로드" 버튼으로 현재 설정을 <code>app-config.json</code> 파일로 저장</li>
                <li>저장된 파일을 프로젝트의 <code>public/config/</code> 폴더에 복사</li>
                <li>서버 재시작 시 해당 파일에서 설정이 자동으로 복구됩니다</li>
              </ol>
              <p className="info-note">
                💡 파일이 없으면 브라우저 저장소의 백업을 사용하며, 둘 다 없으면 기본 설정을 사용합니다.
              </p>
            </div>
          </div>
        </div>

        {/* 서버 설정 탭 */}
        {activeTab === 'server' && (
          <>
        {/* 서버 목록 */}
        <div className="servers-section">
          <div className="section-header">
            <h3>등록된 서버 목록</h3>
            <button onClick={handleAddServer} className="btn-add-server">
              + 서버 추가
            </button>
          </div>

          <div className="servers-list">
            {servers.map(server => (
              <div key={server.id} className={`server-card ${server.isActive ? 'active' : ''}`}>
                <div className="server-header">
                  <div className="server-info">
                    <h4>{server.name}</h4>
                    <span className="server-url">{server.baseUrl}</span>
                  </div>
                  <div className="server-actions">
                    <button
                      onClick={() => toggleServerActive(server.id)}
                      className={`btn-toggle ${server.isActive ? 'active' : ''}`}
                    >
                      {server.isActive ? '활성' : '비활성'}
                    </button>
                    <button onClick={() => handleTestConnection(server)} className="btn-test-small">
                      테스트
                    </button>
                    <button onClick={() => handleEditServer(server.id)} className="btn-edit">
                      편집
                    </button>
                    <button onClick={() => handleDeleteServer(server.id)} className="btn-delete">
                      삭제
                    </button>
                  </div>
                </div>
                <div className="server-details">
                  <span>도메인: {server.domain}</span>
                  <span>타임아웃: {server.apiTimeout}ms</span>
                  <span>ID: {server.id}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 서버 추가/편집 폼 */}
        {(isAddingServer || editingServerId) && (
          <div className="server-form-section">
            <div className="section-header">
              <h3>{editingServerId ? '서버 편집' : '서버 추가'}</h3>
              <button onClick={resetForm} className="btn-cancel">
                취소
              </button>
            </div>

            <div className="settings-form">
              <div className="form-row">
                <div className="setting-group">
                  <label htmlFor="serverId">서버 ID</label>
                  <input
                    id="serverId"
                    type="text"
                    value={formData.id}
                    onChange={(e) => handleInputChange('id', e.target.value)}
                    placeholder="trading-server"
                    className="setting-input"
                    disabled={!!editingServerId}
                  />
                  <small className="setting-help">
                    API 호출 시 서버를 식별하는 고유 ID
                  </small>
                </div>

                <div className="setting-group">
                  <label htmlFor="serverName">서버 이름</label>
                  <input
                    id="serverName"
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="거래 데이터 서버"
                    className="setting-input"
                  />
                  <small className="setting-help">
                    사용자가 식별하기 쉬운 서버 이름
                  </small>
                </div>
              </div>

              <div className="form-row">
                <div className="setting-group">
                  <label htmlFor="baseUrl">서버 주소 (Base URL)</label>
                  <input
                    id="baseUrl"
                    type="text"
                    value={formData.baseUrl}
                    onChange={(e) => handleInputChange('baseUrl', e.target.value)}
                    placeholder="http://localhost:8080"
                    className="setting-input"
                  />
                  <small className="setting-help">
                    예: http://localhost:8080, https://api.example.com
                  </small>
                </div>

                <div className="setting-group">
                  <label htmlFor="domain">도메인</label>
                  <input
                    id="domain"
                    type="text"
                    value={formData.domain}
                    onChange={(e) => handleInputChange('domain', e.target.value)}
                    placeholder="localhost"
                    className="setting-input"
                  />
                  <small className="setting-help">
                    서버의 도메인명 또는 IP 주소
                  </small>
                </div>
              </div>

              <div className="form-row">
                <div className="setting-group">
                  <label htmlFor="apiTimeout">API 타임아웃 (밀리초)</label>
                  <input
                    id="apiTimeout"
                    type="number"
                    value={formData.apiTimeout}
                    onChange={(e) => handleInputChange('apiTimeout', parseInt(e.target.value))}
                    placeholder="10000"
                    className="setting-input"
                    min="1000"
                    max="60000"
                    step="1000"
                  />
                  <small className="setting-help">
                    API 요청 타임아웃 시간 (1초 ~ 60초)
                  </small>
                </div>

                <div className="setting-group checkbox-group">
                  <label htmlFor="isActive" className="checkbox-label">
                    <input
                      id="isActive"
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => handleInputChange('isActive', e.target.checked)}
                      className="setting-checkbox"
                    />
                    활성화
                  </label>
                  <small className="setting-help">
                    체크 시 이 서버를 활성 상태로 설정
                  </small>
                </div>
              </div>
            </div>

            <div className="settings-actions">
              <button onClick={handleSaveServer} className="btn-save">
                {editingServerId ? '수정' : '추가'}
              </button>
            </div>
          </div>
        )}

          </>
        )}
        
        {/* 거래대금 표 설정 탭 */}
        {activeTab === 'table' && (
          <>
            
            {/* 종목명 필터 설정 */}
            <div className="table-settings-section">
              <div className="section-header">
                <h3>종목명 필터 설정</h3>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={tableSettings.stockNameFilter.enabled}
                    onChange={(e) => handleTableSettingChange('stockNameFilter', { enabled: e.target.checked })}
                  />
                  <span className="slider"></span>
                </label>
              </div>
              
              {tableSettings.stockNameFilter.enabled && (
                <div className="filter-settings">
                  <div className="filter-mode">
                    <label>필터 모드:</label>
                    <select 
                      value={tableSettings.stockNameFilter.mode}
                      onChange={(e) => handleTableSettingChange('stockNameFilter', { mode: e.target.value })}
                      className="mode-select"
                    >
                      <option value="exclude">제외 (선택한 키워드 포함 종목 숨김)</option>
                      <option value="include">포함 (선택한 키워드 포함 종목만 표시)</option>
                    </select>
                  </div>
                  
                  <div className="case-sensitive">
                    <label>
                      <input
                        type="checkbox"
                        checked={tableSettings.stockNameFilter.caseSensitive}
                        onChange={(e) => handleTableSettingChange('stockNameFilter', { caseSensitive: e.target.checked })}
                      />
                      대소문자 구분
                    </label>
                  </div>
                  
                  <div className="keyword-input">
                    <div className="input-with-button">
                      <input
                        type="text"
                        value={newFilterKeyword}
                        onChange={(e) => setNewFilterKeyword(e.target.value)}
                        placeholder="필터링할 키워드 입력"
                        onKeyPress={(e) => e.key === 'Enter' && handleAddFilterKeyword()}
                        className="keyword-input-field"
                      />
                      <button onClick={handleAddFilterKeyword} className="add-keyword-btn">
                        추가
                      </button>
                    </div>
                  </div>
                  
                  <div className="keywords-list">
                    <div className="keywords-header">
                      <span>등록된 키워드 ({tableSettings.stockNameFilter.keywords.length}개)</span>
                      {tableSettings.stockNameFilter.keywords.length > 0 && (
                        <button onClick={clearFilterKeywords} className="clear-all-btn">
                          모두 삭제
                        </button>
                      )}
                    </div>
                    <div className="keywords-tags">
                      {tableSettings.stockNameFilter.keywords.map((keyword, index) => (
                        <span key={index} className="keyword-tag">
                          {keyword}
                          <button 
                            onClick={() => handleRemoveFilterKeyword(keyword)}
                            className="remove-keyword-btn"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* 테마명 필터 설정 */}
            <div className="table-settings-section">
              <div className="section-header">
                <h3>테마명 필터 설정</h3>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={tableSettings.themeFilter.enabled}
                    onChange={(e) => handleTableSettingChange('themeFilter', { enabled: e.target.checked })}
                  />
                  <span className="slider"></span>
                </label>
              </div>
              
              {tableSettings.themeFilter.enabled && (
                <div className="filter-settings">
                  <div className="filter-mode">
                    <label>필터 모드:</label>
                    <select 
                      value={tableSettings.themeFilter.mode}
                      onChange={(e) => handleTableSettingChange('themeFilter', { mode: e.target.value })}
                      className="mode-select"
                    >
                      <option value="exclude">제외 (선택한 키워드 포함 테마 숨김)</option>
                      <option value="include">포함 (선택한 키워드 포함 테마만 표시)</option>
                    </select>
                  </div>
                  
                  <div className="case-sensitive">
                    <label>
                      <input
                        type="checkbox"
                        checked={tableSettings.themeFilter.caseSensitive}
                        onChange={(e) => handleTableSettingChange('themeFilter', { caseSensitive: e.target.checked })}
                      />
                      대소문자 구분
                    </label>
                  </div>
                  
                  <div className="keyword-input">
                    <div className="input-with-button">
                      <input
                        type="text"
                        value={newThemeFilterKeyword}
                        onChange={(e) => setNewThemeFilterKeyword(e.target.value)}
                        placeholder="필터링할 테마 키워드 입력"
                        onKeyPress={(e) => e.key === 'Enter' && handleAddThemeFilterKeyword()}
                        className="keyword-input-field"
                      />
                      <button onClick={handleAddThemeFilterKeyword} className="add-keyword-btn">
                        추가
                      </button>
                    </div>
                  </div>
                  
                  <div className="keywords-list">
                    <div className="keywords-header">
                      <span>등록된 키워드 ({tableSettings.themeFilter.keywords.length}개)</span>
                      {tableSettings.themeFilter.keywords.length > 0 && (
                        <button onClick={clearThemeFilterKeywords} className="clear-all-btn">
                          모두 삭제
                        </button>
                      )}
                    </div>
                    <div className="keywords-tags">
                      {tableSettings.themeFilter.keywords.map((keyword, index) => (
                        <span key={index} className="keyword-tag">
                          {keyword}
                          <button 
                            onClick={() => handleRemoveThemeFilterKeyword(keyword)}
                            className="remove-keyword-btn"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* 표시 컬럼 설정 */}
            <div className="table-settings-section">
              <div className="section-header">
                <h3>표시 컬럼 설정</h3>
              </div>
              <div className="columns-grid">
                {Object.entries({
                  rank: '순위',
                  name: '종목명', 
                  price: '현재가',
                  rate: '등락률',
                  tradingValue: '거래대금',
                  volume: '거래량',
                  marketCap: '시총',
                  theme: '테마'
                }).map(([key, label]) => (
                  <label key={key} className="column-checkbox">
                    <input
                      type="checkbox"
                      checked={tableSettings.visibleColumns[key]}
                      onChange={(e) => handleTableSettingChange('visibleColumns', { [key]: e.target.checked })}
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </div>
            
            {/* 테마 표시 설정 */}
            <div className="table-settings-section">
              <div className="section-header">
                <h3>테마 표시 설정</h3>
              </div>
              <div className="theme-settings">
                <label>
                  <input
                    type="checkbox"
                    checked={tableSettings.themeDisplay.showAll}
                    onChange={(e) => handleTableSettingChange('themeDisplay', { showAll: e.target.checked })}
                  />
                  모든 테마 표시
                </label>
                
                {!tableSettings.themeDisplay.showAll && (
                  <div className="max-themes">
                    <label>최대 표시 개수:</label>
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={tableSettings.themeDisplay.maxThemes}
                      onChange={(e) => handleTableSettingChange('themeDisplay', { maxThemes: parseInt(e.target.value) })}
                      className="number-input"
                    />
                  </div>
                )}
              </div>
            </div>
            
            {/* 색상 설정 */}
            <div className="table-settings-section">
              <div className="section-header">
                <h3>등락률 색상 설정</h3>
              </div>
              <div className="color-settings">
                <div className="color-item">
                  <label>상승:</label>
                  <input
                    type="color"
                    value={tableSettings.colorScheme.positiveRate}
                    onChange={(e) => handleTableSettingChange('colorScheme', { positiveRate: e.target.value })}
                    className="color-input"
                  />
                </div>
                <div className="color-item">
                  <label>하락:</label>
                  <input
                    type="color"
                    value={tableSettings.colorScheme.negativeRate}
                    onChange={(e) => handleTableSettingChange('colorScheme', { negativeRate: e.target.value })}
                    className="color-input"
                  />
                </div>
                <div className="color-item">
                  <label>보합:</label>
                  <input
                    type="color"
                    value={tableSettings.colorScheme.neutralRate}
                    onChange={(e) => handleTableSettingChange('colorScheme', { neutralRate: e.target.value })}
                    className="color-input"
                  />
                </div>
              </div>
            </div>
            
            {/* 설정 초기화 */}
            <div className="table-settings-section">
              <div className="section-header">
                <h3>설정 관리</h3>
              </div>
              <div className="settings-actions">
                <button 
                  onClick={() => {
                    if (window.confirm('거래대금 표 설정을 모두 초기화하시겠습니까?')) {
                      resetTableSettings();
                      setSaveMessage('거래대금 표 설정이 초기화되었습니다.');
                      setTimeout(() => setSaveMessage(''), 3000);
                    }
                  }}
                  className="btn-reset"
                >
                  설정 초기화
                </button>
              </div>
            </div>
          </>
        )}

        {saveMessage && (
          <div className={`save-message ${saveMessage.includes('성공') ? 'success' : 'error'}`}>
            {saveMessage}
          </div>
        )}

        {autoSaveMessage && (
          <div className={`save-message auto-save ${autoSaveMessage.includes('✅') ? 'success' : 'error'}`}>
            {autoSaveMessage}
          </div>
        )}
      </div>
    </div>
  );
};

export default SettingsPage;